import { twMerge } from "tailwind-merge";
import { Controller, type ArrayPath, type Control, type FieldErrors, type FieldValues, type Path } from "react-hook-form";

import type { FieldSize } from "ui/forms";

import FormBuilderActionButton, {
  type FormBuilderActions,
} from "containers/admin/clients/client/components/form-builder/form-builder-action-button";
import { FormBuilderDateRangeField } from "containers/admin/clients/client/components/form-builder/form-builder-date-range-field";
import { FormBuilderDynamicSection } from "containers/admin/clients/client/components/form-builder/form-builder-dynamic-section";
import { renderControllerField } from "containers/admin/clients/client/components/form-builder/form-builder-field-renderers";
import type { RenderFieldFn } from "containers/admin/clients/client/components/form-builder/form-builder-field.types";
import { renderGroupedPropertyEntries } from "containers/admin/clients/client/components/form-builder/form-builder-grouped-fields";
import { renderSectionHeading } from "containers/admin/clients/client/components/form-builder/form-builder-section-heading";
import {
  getConditionalDependentFieldNames,
  getConditionalRules,
  getDateRanges,
  getFieldMask,
  getFieldOptions,
  getFieldPlaceholder,
  getFieldTitle,
  getFieldUiType,
  isActionButtonField,
  isFieldDisabled,
  isFullWidthField,
  leadsWithDateRange,
  matchesConditionalShowIf,
  sortJsonFormFields,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import {
  getFieldGenerationTooltip,
  getNestedValueByPath,
  getObjectHeadingLevel,
  isObjectSchema,
  isRequiredField,
} from "containers/admin/clients/client/components/form-builder/form-builder-utils";

const sortFields = sortJsonFormFields;

/** Figma's Medium (32px) field, which is MUI's `small`. */
const FIELD_SIZE: FieldSize = "small";

/**
 * Mirrors `FormBuilderDynamicSection`'s own "render nothing" condition (no add button, no empty
 * message, no rows) so a parent object wrapping only such a section — e.g. a channel's "SMS"
 * heading around its dynamic-section of rows — can skip rendering itself, heading included,
 * instead of showing a title over an invisible section.
 */
const isEmptyNonUserManagedDynamicSection = (field: JsonFormSchema, value: unknown): boolean => {
  if (field.type !== "array" || getFieldUiType(field) !== "dynamic-section") {
    return false;
  }

  const dynamicSectionConfig = field["x-dynamic-section"];

  return (
    (!Array.isArray(value) || value.length === 0) &&
    !dynamicSectionConfig?.addButtonLabel &&
    !dynamicSectionConfig?.emptyMessage
  );
};

type CreateFormBuilderFieldsParams<T extends FieldValues> = {
  control: Control<T>;
  errors: FieldErrors<T>;
  formData: unknown;
  actions?: FormBuilderActions;
};

export const createFormBuilderFields = <T extends FieldValues>({
  control,
  errors,
  formData,
  actions,
}: CreateFormBuilderFieldsParams<T>) => {
  const renderConditionalFields = (
    name: string,
    objectSchema: JsonFormObjectSchema,
    prefix = "",
  ) =>
    getConditionalRules(objectSchema).map((rule) => {
      if (rule.triggerFieldName !== name) {
        return null;
      }

      const triggerFieldPath = prefix ? `${prefix}.${name}` : name;
      const currentFieldValue = getNestedValueByPath(formData, triggerFieldPath);
      const isShown = matchesConditionalShowIf(currentFieldValue, rule.showIf);

      if (!isShown) {
        return null;
      }

      // Fields flagged `x-full-width` break out of this inline rendering entirely — they render as
      // their own full-width row via `renderGroupedPropertyEntries`'s breakout (see
      // `form-builder-grouped-fields.tsx`), a level up from the trigger's own grid cell.
      const inlineDependentEntries = sortFields(
        rule.dependentFieldNames.flatMap((dependentFieldName) => {
          const dependentField = objectSchema.properties[dependentFieldName];

          return dependentField && !isFullWidthField(dependentField)
            ? [[dependentFieldName, dependentField] as [string, JsonFormSchema]]
            : [];
        }),
      );

      if (inlineDependentEntries.length === 0) {
        return null;
      }

      return (
        <div
          key={`${triggerFieldPath}-${String(rule.showIf)}`}
          className="flex flex-col gap-2.5"
        >
          {renderGroupedPropertyEntries(
            objectSchema,
            prefix,
            inlineDependentEntries,
            ([dependentFieldName, dependentField]) =>
              renderField(dependentFieldName, dependentField, objectSchema, prefix),
          )}
        </div>
      );
    });

  const renderProperties = (objectSchema: JsonFormObjectSchema, prefix = "") => {
    const objectDependentFieldNames = getConditionalDependentFieldNames(objectSchema);
    const entries = sortFields(Object.entries(objectSchema.properties)).filter(
      ([name]) => !objectDependentFieldNames.has(name),
    );

    return renderGroupedPropertyEntries(
      objectSchema,
      prefix,
      entries,
      ([name, field]) => renderField(name, field, objectSchema, prefix),
      (fieldName) => getNestedValueByPath(formData, prefix ? `${prefix}.${fieldName}` : fieldName),
    );
  };

  const renderField: RenderFieldFn = (name, field, parentSchema, prefix = "") => {
    const fieldName = (prefix ? `${prefix}.${name}` : name) as Path<T>;
    const fieldPath = prefix ? `${prefix}.${name}` : name;
    const error = getNestedValueByPath(errors, fieldPath) as
      | FieldErrors<T>[Path<T>]
      | undefined;
    const label = getFieldTitle(name, field);
    const uiType = getFieldUiType(field);
    const options = getFieldOptions(field);
    const mask = getFieldMask(field);
    const showFieldLabel = Boolean(label?.trim());

    const commonProps = {
      name: fieldName,
      label,
      placeholder: getFieldPlaceholder(field),
      errMessage: error?.message as string | undefined,
      helperText: field.description,
      required: isRequiredField(parentSchema, name, formData, prefix),
      disabled: isFieldDisabled(field),
      size: FIELD_SIZE,
    };

    if (isObjectSchema(field)) {
      const headingLevel = getObjectHeadingLevel(field);
      const isEmptyObjectSection = Object.entries(field.properties).every(([childName, childField]) =>
        isEmptyNonUserManagedDynamicSection(
          childField,
          getNestedValueByPath(formData, `${fieldPath}.${childName}`),
        ),
      );

      if (isEmptyObjectSection) {
        return null;
      }

      return (
        <div
          key={fieldName}
          className={twMerge(
            "flex flex-col",
            headingLevel === "section" ? "gap-4" : "gap-1.5",
          )}
        >
          {label.trim() && !leadsWithDateRange(field) && renderSectionHeading(label, headingLevel)}
          <div className="flex flex-col gap-4">{renderProperties(field, fieldPath)}</div>
        </div>
      );
    }

    if (isActionButtonField(field)) {
      return (
        <FormBuilderActionButton
          key={fieldName}
          name={name}
          field={field}
          actions={actions}
        />
      );
    }

    if (field.type === "array" && uiType === "dynamic-section") {
      return (
        <FormBuilderDynamicSection
          key={fieldName}
          fieldName={fieldName as ArrayPath<T>}
          field={field}
          control={control}
          renderField={renderField}
        />
      );
    }

    const dateRange = getDateRanges(parentSchema).find((rangeNames) => rangeNames.includes(name));

    if (dateRange) {
      const [startName, endName] = dateRange;
      const endField = parentSchema.properties[endName];

      if (name === endName || !endField) {
        return null;
      }

      const endPath = prefix ? `${prefix}.${endName}` : endName;
      const endError = getNestedValueByPath(errors, endPath) as { message?: string } | undefined;

      return (
        <FormBuilderDateRangeField
          key={fieldName}
          control={control}
          startName={fieldName}
          endName={endPath as Path<T>}
          groupLabel={leadsWithDateRange(parentSchema) ? parentSchema.title : undefined}
          startLabel={label}
          endLabel={getFieldTitle(endName, endField)}
          errMessage={[commonProps.errMessage, endError?.message]}
          required={commonProps.required || isRequiredField(parentSchema, endName, formData, prefix)}
          disabled={commonProps.disabled}
          size={commonProps.size}
          infoTooltip={getFieldGenerationTooltip(parentSchema, startName)}
        />
      );
    }

    return (
      <div
        key={fieldName}
        className={twMerge(
          "flex flex-col",
          uiType !== "checkbox-group" || showFieldLabel ? "gap-2.5" : undefined,
        )}
      >
        <Controller
          name={fieldName}
          control={control}
          render={({ field: controllerField }) =>
            renderControllerField<T>({
              uiType,
              field,
              parentSchema,
              name,
              prefix,
              fieldName,
              label,
              options,
              mask,
              showFieldLabel,
              commonProps,
              error,
              renderField,
              controllerField,
            })
          }
        />
        {uiType !== "radio-group" && renderConditionalFields(name, parentSchema, prefix)}
      </div>
    );
  };

  return { renderField, renderProperties, renderConditionalFields };
};
